import Theme from 'src/components/styled/theme/theme';
import { colors } from 'src/components/styled/theme/constants/colors';
import { SHADOW_S, MODAL_SHADOW, MODAL_OVERLAY_BG } from 'src/components/styled/theme/shadows';
import { sharedTypography } from 'src/components/styled/theme/shared/typography';
import { buildSurfaceCssVariables, sharedShape } from 'src/components/styled/theme/shared/surface';
import { categoricalDark } from 'src/components/styled/theme/shared/categorical';

/** Surfaces, fields, and text for analysis-tool UI (see shared/surface.js). */
const surface = {
    // v2 dark Neutral / Blue surfaces
    cardBg: colors.Neutral12,
    cardBorder: colors.Neutral10,
    fieldBg: colors.Neutral11,
    fieldBorder: colors.Neutral10,
    textPrimary: colors.Neutral1,
    textSecondary: colors.Neutral3,
    textMuted: colors.Neutral5,
    accent: colors.Blue3,
    dangerText: colors.Red2,
    tableHeaderBg: colors.Neutral11,
};

export const darkTheme = new Theme({
    components: {
        callout_banner: {
            background: colors.Neutral11,
        },
        lyt_map_info_card: {
            background: colors.Neutral12,
            border: colors.Neutral10,
        },
        lyt_input: {
            background: {
                default: colors.Neutral12,
                hover: colors.Neutral11,
                focus: colors.Neutral12,
                error: colors.Neutral12,
            },
            border: {
                default: colors.Neutral8,
                focus: colors.Blue3,
                hover: colors.Neutral8,
                error: colors.Red3,
            }
        },
        lyt_textarea: {
            background: {
                default: colors.Neutral12,
                hover: '#343436', // Dark hover background
                focus: colors.Neutral12,
                error: colors.Neutral12,
                disabled: colors.Neutral10,
            },
            border: {
                default: colors.Neutral8,
                focus: '#B6D2FF', // Dark focused/selected border
                hover: colors.Neutral8,
                error: '#C8322A', // Dark invalid border
                disabled: colors.Neutral9,
            },
            text: {
                default: colors.Neutral0,
                placeholder: colors.Neutral6,
                disabled: colors.Neutral7,
            }
        },
        lyt_checkbox: {
            border: {
                default: colors.Neutral7,
                hover: colors.Neutral7,
                pressed: colors.Neutral7,
                focus: colors.Blue5,
                error: colors.Red3,
            },
            background: {
                default: colors.Transparent,
                hover: colors.Neutral3,
                pressed: colors.Neutral4,
                focus: colors.Neutral0,
                error: colors.Neutral0,
            },
            checkmark: {
                default: colors.Blue3,
            },
            text: {
                selected: colors.Blue3,
            }
        },
        lyt_radio: {
            border: {
                default: colors.Neutral4,
                focus: colors.Blue2,
                selected: colors.Blue3,
                disabled: colors.Neutral9,
                danger: colors.Red2,
            },
            background: {
                default: colors.Neutral12,
                hover: colors.Neutral11,
                active: colors.Blue6,
                selected: colors.Neutral12,
                disabled: colors.Neutral11,
                danger: colors.Red2,
            },
            dot: {
                selected: colors.Neutral12,
                disabled: colors.Neutral7,
                danger: colors.Neutral12,
            },
            label: {
                default: colors.Neutral0,
                disabled: colors.Neutral8,
            }
        },
        lyt_toggle: {
            background: {
                default: colors.Neutral8,
                hover: colors.Neutral7,
                toggled: colors.Green4,
                toggledHover: colors.Green5,
                toggledDisabled: colors.Green0,
                disabled: 'rgba(124, 125, 132, 0.30)',
            },
            thumb: {
                background: colors.Neutral1,
            },
            label: {
                color: colors.Neutral0,
                disabled: colors.Neutral8,
            },
            sublabel: {
                color: colors.Neutral5, // Lighter color for dark theme visibility
            },
            border: {
                focus: colors.Blue4,
            }
        },
        lyt_dropdown: {
            content: {
                background: colors.Neutral11,
                boxshadow: SHADOW_S,
                border: colors.Neutral10,
            },
        },
        lyt_tag: {
            background: {
                default: colors.Neutral11,
                hover: colors.Neutral10,
                pressed: colors.Neutral9,
                focus: colors.Neutral11,
            },
            text: {
                default: colors.Neutral1,
            },
            border: {
                focus: colors.Blue2,
            }
        },
        lyt_removeable_tag: {
            background: {
                default: colors.Neutral11,
                hover: colors.Red3,
                pressed: colors.Red2,
                focus: colors.Neutral11,
            },
            text: {
                default: colors.Neutral1,
            },
            border: {
                focus: colors.Blue2,
            }
        },
        lyt_badge: {
            default: {
                background: colors.Neutral11,
                border: colors.Transparent,
                text: colors.Neutral3,
            },
            'inverted-default': {
                background: colors.Transparent,
                border: colors.Neutral3,
                text: colors.Neutral3,
            },
            primary: {
                background: colors.Blue3,
                border: colors.Transparent,
                text: colors.Neutral12,
            },
            'inverted-primary': {
                background: colors.Transparent,
                border: colors.Blue3,
                text: colors.Blue3,
            },
            success: {
                background: colors.Green7,
                border: colors.Transparent,
                text: colors.Green0,
            },
            warning: {
                background: colors.Yellow6,
                border: colors.Transparent,
                text: colors.Yellow0,
            },
            important: {
                background: colors.Red6,
                border: colors.Transparent,
                text: colors.Red0,
            },
        },
        lyt_form: {
            background: colors.Neutral11,
            border: colors.Neutral10,
            shadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
            title: {
                color: colors.Neutral1,
            },
            subtitle: {
                color: colors.Neutral6,
            },
        },
        lyt_menu_item: {
            color: {
                default: colors.Neutral1,
                selected: colors.Blue3,
                disabled: colors.Neutral6,
                danger: colors.Red0,
            },
            icon: {
                default: colors.Neutral3,
                selected: colors.Blue3,
                disabled: colors.Neutral6,
                danger: colors.Red0,
            },
            background: {
                default: colors.Neutral11,
                hover: colors.Neutral10,
                pressed: colors.Neutral9,
                focus: colors.Neutral11,
                selected: colors.Blue7,
                selectedHover: colors.Blue6,
                selectedPressed: colors.Blue5,
                selectedFocus: colors.Blue7,
                disabled: colors.Neutral10,
            },
            border: {
                selected: colors.Blue3,
            },
            outline: {
                focus: colors.Blue3,
            }
        },
        lyt_button: {
            disabled_background: colors.Neutral7,
            primary: {
                background: {
                    default: colors.Blue3,
                    hover: colors.Blue2,
                    pressed: colors.Blue5,
                    focus: colors.Blue3,
                    disabled: colors.Neutral7,
                    selected: colors.Blue0,
                },
                border: {
                    focus: colors.Blue4,
                },
                text: {
                    default: colors.Neutral11,
                    hover: colors.Neutral11,
                    pressed: colors.Neutral11,
                    focus: colors.Neutral11,
                    disabled: colors.Neutral2,
                    selected: colors.Blue3,
                }
            },
            secondary: {
                background: {
                    default: colors.Neutral11,
                    hover: colors.Neutral10,
                    pressed: colors.Neutral9,
                    focus: colors.Neutral11,
                    disabled: colors.Neutral7,
                    selected: colors.Blue7,
                },
                border: {
                    focus: colors.Blue2,
                },
                text: {
                    default: colors.Neutral3,
                    hover: colors.Neutral3,
                    pressed: colors.Neutral3,
                    focus: colors.Neutral3,
                    disabled: colors.Neutral3,
                    selected: colors.Blue3,
                }
            },
            tertiary: {
                background: {
                    default: colors.Transparent,
                    hover: colors.Neutral10,
                    pressed: colors.Neutral9,
                    focus: colors.Transparent,
                    disabled: colors.Transparent,
                    selected: colors.Blue7,
                },
                border: {
                    default: colors.Neutral8,
                    hover: colors.Neutral8,
                    focus: colors.Neutral8,
                    pressed: colors.Neutral8,
                    disabled: colors.Neutral7,
                    selected: colors.Blue7,
                    outline: colors.Blue2,
                },
                text: {
                    default: colors.Neutral3,
                    hover: colors.Neutral3,
                    pressed: colors.Neutral3,
                    focus: colors.Neutral3,
                    disabled: colors.Neutral5,
                    selected: colors.Blue2,
                }
            },
            invisible: {
                background: {
                    default: colors.Transparent,
                    hover: colors.Neutral10,
                    pressed: colors.Neutral9,
                    focus: colors.Neutral11,
                    disabled: colors.Transparent,
                    selected: colors.Blue7,
                },
                border: {
                    focus: colors.Blue2,
                },
                text: {
                    default: colors.Neutral3,
                    hover: colors.Neutral3,
                    pressed: colors.Neutral3,
                    focus: colors.Neutral3,
                    disabled: colors.Neutral3,
                    selected: colors.Blue3,
                }
            },
            toast: {
                background: {
                    default: '#F8F9FC59',
                    hover: '#F8F9FC80',
                    pressed: '#F8F9FC99',
                    focus: '#F8F9FC59',
                    disabled: colors.Neutral5,
                    selected: '#F8F9FC80',
                },
                border: {
                    outline: colors.Blue2,
                },
                text: {
                    default: colors.Neutral1,
                    hover: colors.Neutral1,
                    pressed: colors.Neutral1,
                    focus: colors.Neutral1,
                    disabled: colors.Neutral5,
                    selected: colors.Neutral1,
                }
            },
            toast_close: {
                background: {
                    default: colors.Transparent,
                    hover: colors.Transparent,
                    pressed: colors.Transparent,
                    focus: colors.Transparent,
                    disabled: colors.Transparent,
                    selected: colors.Transparent,
                },
                border: {
                    focus: colors.Blue2,
                },
                text: {
                    default: colors.Neutral3,
                    hover: colors.Neutral3,
                    pressed: colors.Neutral3,
                    focus: colors.Neutral3,
                    disabled: colors.Neutral5,
                    selected: colors.Neutral3,
                }
            },
            danger: {
                background: {
                    default: colors.Red2,
                    hover: colors.Red1,
                    pressed: colors.Red0,
                    focus: colors.Red2,
                    disabled: colors.Neutral5,
                    selected: colors.Red0,
                },
                border: {
                    focus: colors.Red2,
                },
                text: {
                    default: colors.Neutral1,
                    hover: colors.Neutral1,
                    pressed: colors.Neutral1,
                    focus: colors.Neutral1,
                    disabled: colors.Neutral6,
                    selected: colors.Red3,
                }
            },
        },
        linkTab: {
            active: {
                background: colors.Blue2,
                text: colors.Blue2,
            },
            color: colors.Neutral0,
        },
        dropdown: {
            input: {
                background: colors.Neutral12,
                text: colors.Neutral1,
                placeholder: colors.Neutral6,
                border: {
                    default: colors.Neutral11,
                    focus: colors.Blue2,
                    hover: colors.Neutral10
                },
                icon: colors.Neutral6,
                clearButton: {
                    background: colors.Neutral1,
                    hover: colors.Neutral2
                }
            },
            list: {
                background: colors.Neutral13,
                border: colors.Neutral11,
                shadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
                scrollbar: {
                    track: colors.Neutral12,
                    thumb: {
                        default: colors.Neutral10,
                        hover: colors.Neutral9,
                        active: colors.Neutral8
                    }
                },
                item: {
                    text: colors.Neutral1,
                    background: colors.Transparent,
                    hover: {
                        text: colors.Blue2,
                        background: colors.Neutral11
                    },
                    active: {
                        text: colors.Blue2,
                        background: colors.Neutral11
                    }
                },
                noResults: {
                    text: colors.Neutral6
                }
            },
            transition: {
                duration: '0.2s',
                timing: 'ease-in-out'
            }
        },
        table: {
            row: {
                evenBackground: colors.Neutral9,
                oddBackground: colors.Neutral11,
                text: colors.Neutral1,
            },
            border: colors.Neutral8,
        },
        dynamicTable: {
            headerBackground: colors.Neutral12,
        },
        sidebar: {
            section_title: {
                color: colors.Neutral1,
            },
            borderRight: colors.Neutral8,
            dropdown: {
                background: colors.Neutral12,
                text: colors.Neutral1,
                subtext: colors.Neutral6,
                hover: {
                    background: colors.Neutral11,
                    text: colors.Neutral0,
                    subtext: colors.Neutral4,
                    icon: {
                        color: colors.Blue2
                    }
                },
                active: {
                    background: colors.Neutral10,
                    text: colors.Blue3,
                    icon: {
                        color: colors.Blue3
                    }
                },
                panel: {
                    background: colors.Neutral13,
                    shadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
                    border: `1px solid ${colors.Neutral11}`,
                    zIndex: 1050
                },
                option: {
                    text: colors.Neutral1,
                    hover: {
                        background: colors.Neutral11,
                        text: colors.Blue2
                    },
                    selected: {
                        background: colors.Neutral11,
                        text: colors.Blue2,
                        checkmark: colors.Blue2
                    }
                },
                icon: {
                    color: colors.Neutral4, // Lighter color for dark mode sidebar icons
                    hover: colors.Blue2
                },
                transition: {
                    duration: '0.2s',
                    timing: 'ease-in-out'
                },
                collapsed: {
                    background: colors.Neutral12,
                    hover: {
                        background: colors.Neutral11,
                        icon: {
                            color: colors.Blue2
                        }
                    }
                }
            },
            background: colors.Neutral12,
            logo: {
                primary: colors.Neutral0,
                secondary: colors.Blue3,
            },
        },
        mapOptions: {
            background: colors.Neutral13,
            text: colors.Neutral1,
            border: colors.Neutral11,
            radio: {
                unchecked: colors.Neutral6,
                checked: colors.Blue2,
                hover: colors.Neutral11
            },
            collapse: {
                background: colors.Neutral12,
                border: colors.Neutral11
            },
            agencySelector: {
                label: colors.Neutral1,
                input: {
                    background: colors.Neutral12,
                    text: colors.Neutral1,
                    border: colors.Neutral11,
                    placeholder: colors.Neutral6
                },
                autocomplete: {
                    background: colors.Neutral13,
                    hover: {
                        background: colors.Neutral11,
                        text: colors.Blue2
                    },
                    border: colors.Neutral11,
                    shadow: '0 2px 8px rgba(0, 0, 0, 0.25)'
                }
            },
            routeSelector: {
                background: colors.Neutral12,
                text: colors.Neutral1,
                border: colors.Neutral11,
                placeholder: colors.Neutral6,
                multiValue: {
                    background: colors.Blue1,
                    text: colors.Blue3,
                    removeButton: {
                        color: colors.Blue3,
                        hover: colors.Blue2
                    }
                },
                dropdown: {
                    background: colors.Neutral13,
                    hoverBackground: colors.Neutral11,
                    hoverText: colors.Blue2,
                    shadow: '0 2px 8px rgba(0, 0, 0, 0.25)'
                }
            }
        },
        divider: {
            background: colors.Neutral8, // Darker gray divider for dark theme
            opacity: 0.8,
        },
        performancePanel: {
            background: colors.Neutral13,
            text: colors.Neutral1,
            border: colors.Neutral11,
            table: {
                header: {
                    background: colors.Neutral12,
                    text: colors.Neutral1
                },
                cell: {
                    text: colors.Neutral1
                }
            },
            statusColors: {
                green: '#01AC01',
                blue: '#0063ff',
                red: '#E95063'
            },
            collapse: {
                background: colors.Neutral12,
                border: colors.Neutral11
            },
            transition: {
                duration: '0.2s',
                timing: 'ease-in-out'
            }
        },
        lyt_tabs: {
            text: {
                default: colors.Neutral5,
                hover: colors.Neutral4,
                selected: colors.Blue3,
                disabled: colors.Neutral8,
            },
            border: {
                default: colors.Neutral9,
                hover: colors.Neutral7,
                selected: colors.Blue3,
            },
            background: {
                default: colors.Transparent,
                hover: colors.Neutral11,
                focus: colors.Transparent,
            },
            outline: {
                focus: colors.Blue3,
            }
        },
        lyt_tooltip: {
            background: colors.Neutral3,
            text: colors.Neutral11,
        },
        lyt_modal: {
            overlay: {
                background: MODAL_OVERLAY_BG, // primary-blue/07 at 12% opacity
            },
            dialog: {
                background: colors.Neutral12,
                text: colors.Neutral1,
                border: colors.Neutral11,
                shadow: MODAL_SHADOW, // L variant — base + 0 40px 80px -16px at 24%
            },
            header: {
                background: colors.Transparent, // Inherit from dialog
                text: colors.Neutral1,
                borderBottom: colors.Neutral11, // Optional divider
            },
            body: {
                background: colors.Transparent, // Inherit from dialog
                text: colors.Neutral2,
            },
            footer: {
                background: colors.Transparent, // Inherit from dialog
                borderTop: colors.Neutral11, // Optional divider
            },
            closeButton: {
                background: colors.Transparent,
                text: colors.Neutral5,
                hover: {
                    background: colors.Neutral11,
                    text: colors.Neutral1,
                }
            }
        },
        lyt_toast: {
            success: {
                background: colors.Green6,
                text: colors.Neutral1,
                subtleText: colors.Neutral1,
                icon: colors.Green0,
            },
            error: {
                background: colors.Red2,
                text: colors.Neutral12,
                subtleText: colors.Neutral12,
                icon: colors.Neutral11,
            },
            warning: {
                background: '#FDE75F', // Yellow warning (unique toast yellow)
                text: colors.Neutral11,
                subtleText: colors.Neutral11,
                icon: colors.Yellow6,
            },
            info: {
                background: colors.Neutral11,
                text: colors.Neutral1,
                subtleText: colors.Neutral1,
                icon: colors.Neutral1,
            },
            normal: {
                background: colors.Neutral10,
                text: colors.Neutral1,
                subtleText: colors.Neutral1,
                border: colors.Neutral9,
                icon: colors.Neutral1,
            },
            shadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        },
        lyt_accordion: {
            header: {
                background: {
                    default: colors.Neutral11,
                    open: colors.Blue7,
                    hover: colors.Neutral10,
                },
                text: {
                    default: colors.Neutral4,
                    open: colors.Blue3,
                },
                icon: {
                    default: colors.Neutral10,
                    open: colors.Blue2,
                },
            },
            content: {
                background: colors.Neutral11,
                text: colors.Neutral1,
            },
        }
    },
    // Background colors - use these for consistent background styling across components
    background: {
        default: colors.Neutral12,
        subtle: colors.Neutral11,
        elevated: colors.Neutral11,
        invert: colors.Neutral1,
        overlay: 'rgba(0, 0, 0, 0.6)', // overlay background
    },
    // Typography text colors - use these for consistent text styling across components
    text: {
        default: colors.Neutral2,
        subtle: colors.Neutral3,
        invert: colors.Neutral12,
        selected: colors.Blue3,
        fixed: colors.Neutral12,
        disabled: colors.Neutral5,
        warning: {
            subtle: colors.Yellow1,
            default: colors.Yellow0, // warning text
        },
        link: {
            default: colors.Blue3,
            hover: colors.Blue2,
            pressed: colors.Blue1,
        },
        danger: {
            default: colors.Red0, // danger/error text
        },
        valid: {
            default: colors.Green0, // success/valid text
        },
    },
    // Default icon color
    icon: {
        default: colors.Neutral3,
    },
    // Import shared typography configuration
    ...sharedTypography,
    surface,
    shape: sharedShape,
    categorical: categoricalDark,
    // CSS Variables
    cssVariables: {
        ...buildSurfaceCssVariables(surface),
        '--color-text-subtle': colors.Neutral5,
        '--color-text-default': colors.Neutral1,
        '--color-text-subtext': colors.Neutral4,
        '--color-text-value': colors.Neutral2,
        '--color-text-error': colors.Red2,
        '--color-text-warning': colors.Yellow2,
        '--elevation-surfaces-primary': colors.Neutral12,
    }
});